import { MarkHeading } from '../../../components/MarkControls';
import * as layout from '../../../components/markLayout.module.css';
import { MarkButton, MarkInput, MarkLabel } from '../../../components/MarkControls';
import React, { useId } from 'react';
import P from '@hero/ui-kit/typography/P';
import type { BlastVariantInput } from '../../../types';
type Props = {
    variants: BlastVariantInput[];
    onChange: (variants: BlastVariantInput[]) => void;
};
const VariantBuilder: React.FC<Props> = ({ variants, onChange }) => {
    const groupId = useId();
    const ratioSum = variants.reduce((sum, v) => sum + v.ratio, 0);
    const ratioValid = ratioSum === 100;
    const labelsUnique =
        new Set(variants.map((v) => v.label.trim().toLowerCase())).size === variants.length;
    const updateVariant = (index: number, patch: Partial<BlastVariantInput>) => {
        const next = variants.map((v, i) => (i === index ? { ...v, ...patch } : v));
        onChange(next);
    };
    const addVariant = () => {
        const usedLabels = new Set(variants.map((variant) => variant.label.trim().toLowerCase()));
        let number = 0;
        let label: string;
        do {
            label =
                number < 26
                    ? `Variant ${String.fromCharCode(65 + number)}`
                    : `Variant ${number + 1}`;
            number++;
        } while (usedLabels.has(label.toLowerCase()));
        onChange([...variants, { label, ratio: 0 }]);
    };
    const removeVariant = (index: number) => {
        if (variants.length <= 1) return;
        onChange(variants.filter((_, i) => i !== index));
    };
    return (
        <fieldset aria-label="Variants" className={layout.fieldset}>
            <MarkHeading
                style={{
                    padding: '0 0.4rem'
                }}
                role="h6"
            >
                Variants
            </MarkHeading>
            {variants.map((variant, index) => (
                <div key={index} className={layout.variantRow}>
                    <div
                        style={{
                            flex: 1
                        }}
                        className={layout.field}
                    >
                        <MarkLabel
                            htmlFor={`${groupId}-label-${index}`}
                            style={{
                                display: 'block',
                                marginBottom: '0.2rem'
                            }}
                        >
                            Label
                        </MarkLabel>
                        <MarkInput
                            id={`${groupId}-label-${index}`}
                            data-field={`variant-label-${index}`}
                            type="text"
                            value={variant.label}
                            onChange={(e) => updateVariant(index, { label: e.currentTarget.value })}
                        />
                    </div>
                    <div className={layout.field}>
                        <MarkLabel
                            htmlFor={`${groupId}-ratio-${index}`}
                            style={{
                                display: 'block',
                                marginBottom: '0.2rem'
                            }}
                        >
                            Ratio %
                        </MarkLabel>
                        <MarkInput
                            id={`${groupId}-ratio-${index}`}
                            data-field={`variant-ratio-${index}`}
                            type="number"
                            min={0}
                            max={100}
                            value={variant.ratio}
                            onChange={(e) =>
                                updateVariant(index, {
                                    ratio: Math.max(
                                        0,
                                        Math.floor(Number(e.currentTarget.value) || 0)
                                    )
                                })
                            }
                        />
                    </div>
                    <MarkButton
                        type="button"
                        onClick={() => removeVariant(index)}
                        disabled={variants.length <= 1}
                        aria-label={`Remove ${variant.label}`}
                    >
                        Remove
                    </MarkButton>
                </div>
            ))}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '0.4rem'
                }}
            >
                <MarkButton type="button" data-field="variant-add" onClick={addVariant}>
                    + Add variant
                </MarkButton>
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: ratioValid ? 'var(--mark-success)' : 'var(--mark-danger)'
                    }}
                >
                    Ratios: {variants.map((v) => v.ratio).join(' + ')} = {ratioSum}
                    {ratioValid ? ' ✓' : ' (must equal 100)'}
                </P>
            </div>
            {!labelsUnique && (
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-danger)',
                        marginTop: '0.4rem'
                    }}
                >
                    Variant labels must be unique.
                </P>
            )}
        </fieldset>
    );
};
export default VariantBuilder;
